/**
 * SR-REC-02 권위 리플레이 서버 재생 폴백(RSK-02 · ARC §11 · FS-14-01 '서버 재생(권위 PH2 — FE-501)').
 *
 * 룸 권위 리플레이는 위치 트랙이 없다(입력 로그만 — SR-REC-01). 셸 재시뮬이 60틱 체크포인트 해시와 어긋나면(FE-501 — 다른 엔진 · 기기)
 * 그 틱부터 **서버가 다시 돌린 자세**(server-track@1 — C-10 렌더 워커 · 제어 평면 …/server-track 경유)를 조각(10초)으로 받아 이어 그린다(스트리밍).
 *  - 지금 조각의 절반을 지나면 다음 조각을 미리 받는다 · 아직 안 왔으면 그 틱에서 기다린다(buffering — 재생 시간은 흐르지 않는다)
 *  - 틱마다 두 프레임(10 Hz) 사이를 보간한다(위치 선형 · 회전 nlerp) — 렌더러의 한 틱 보간은 그대로
 *  - 탐색: 그 틱의 조각을 받는다(뒤로도 — 서버 재생기가 처음부터 다시 돈다)
 *  - 서버 재생을 쓸 수 없으면(503 · 네트워크) 입력 재생으로 되돌아가 계속 본다(server = failed — 교사 안내)
 *  - 2026-09-28: 머리 versions 로 **기록한 빌드가 지금과 다르면(buildDiffers) 재생 전에** 곧바로 서버 재생(틱 0부터 — 권위 리플레이에는
 *    위치 트랙이 없다 · SR-REC-02 '빌드가 다르면 재생 전 즉시 전환'). 빌드를 모르는 옛 리플레이는 전처럼 체크포인트 불일치로만.
 * 재생기 면(RoomPlayer)은 그대로라 화면(RoomReplayView)은 재생 루프를 바꾸지 않는다.
 */
import type * as TrackModule from "@picnic-field/room-sim/track";
import type { ServerTrackChunk, TrackFrame } from "@picnic-field/room-sim/track";
import type { RoomPlayer } from "../types";

type TrackLib = typeof TrackModule;
/** 코덱은 서버 재생으로 넘어갈 때만 불러온다(재생 화면 번들에 fflate 를 넣지 않는다) */
let lib: TrackLib | null = null;
const loadLib = async () => (lib ??= await import("@picnic-field/room-sim/track"));

/** 조각 길이(틱 — 10초) */
export const SERVER_CHUNK_TICKS = 600;

export type ServerTrackFetch = (from: number, to: number) => Promise<Uint8Array>;

export type ServerState = "off" | "loading" | "on" | "failed";

export interface FallbackPlayer extends RoomPlayer {
  /** 지금 재생 방식 — 입력 재생(셸 재시뮬) · 서버 재생 */
  readonly playback: "input" | "server";
  readonly server: ServerState;
  /** 서버 조각을 기다리는 중(재생이 그 틱에 멈춰 있다) */
  readonly buffering: boolean;
  /** 서버 재생으로 넘어간 틱(없으면 null) */
  readonly switchedAt: number | null;
  /** 왜 넘어갔나 — build = 재생 전(빌드가 다름) · hash = 체크포인트 불일치(FE-501) · null = 넘어가지 않음 */
  readonly switchReason: "build" | "hash" | null;
}

interface Opts {
  /** 서버 재생으로 넘어갈 때(텔레메트리 · 해시 보고) — why = build(재생 전) · hash(FE-501) */
  onSwitch?: (tick: number, why: "build" | "hash") => void;
  /** 서버 재생을 쓸 수 없을 때 */
  onFail?: (e: unknown) => void;
}

const chunkStart = (t: number) => Math.floor(t / SERVER_CHUNK_TICKS) * SERVER_CHUNK_TICKS;

/**
 * 셸 재생기에 서버 재생 폴백을 씌운다. fetchChunk 가 null 이면(관찰자 · 가짜 없음) 폴백 없이 그대로.
 */
export function withServerFallback(
  local: RoomPlayer,
  fetchChunk: ServerTrackFetch | null,
  opts: Opts = {},
): FallbackPlayer {
  let playback: "input" | "server" = "input";
  let server: ServerState = "off";
  let switchedAt: number | null = null;
  let switchReason: "build" | "hash" | null = null;
  let tick = 0;
  let disposed = false;
  let lastLocalVersion = 0;
  let layoutBase = 0;
  let firstServerVersion: number | null = null;
  let lastOut: Float32Array | null = null;
  let out: Float32Array | null = null;
  const chunks = new Map<number, ServerTrackChunk>();
  const pending = new Map<number, Promise<ServerTrackChunk | null>>();
  let waiters: (() => void)[] = [];
  const duration = local.durationTicks;

  const fetchAt = (start: number): Promise<ServerTrackChunk | null> => {
    if (!fetchChunk || disposed || start >= duration + 1) return Promise.resolve(null);
    const have = chunks.get(start);
    if (have) return Promise.resolve(have);
    const p0 = pending.get(start);
    if (p0) return p0;
    const to = Math.min(duration, start + SERVER_CHUNK_TICKS);
    const p = Promise.all([fetchChunk(start, to), loadLib()])
      .then(([bytes, L]) => {
        const c = L.decodeServerTrack(bytes);
        if (disposed) return null;
        chunks.set(start, c);
        // 멀리 떨어진 조각은 버린다(메모리 — 앞뒤 둘씩)
        for (const k of [...chunks.keys()])
          if (Math.abs(k - chunkStart(tick)) > SERVER_CHUNK_TICKS * 2) chunks.delete(k);
        if (server === "loading") server = "on";
        const w = waiters;
        waiters = [];
        for (const f of w) f();
        return c;
      })
      .catch((e: unknown) => {
        if (!disposed && playback === "server") fail(e);
        return null;
      })
      .finally(() => pending.delete(start));
    pending.set(start, p);
    return p;
  };

  /** 서버를 쓸 수 없다 — 입력 재생으로 돌아가 지금 틱부터 이어 본다 */
  const fail = (e: unknown) => {
    server = "failed";
    playback = "input";
    const at = tick;
    const w = waiters;
    waiters = [];
    for (const f of w) f();
    void local.seek(at).catch(() => {});
    opts.onFail?.(e);
  };

  /** 틱 t 를 덮는 프레임 a(≤t) · b(>t) */
  const framesAt = (t: number): [TrackFrame, TrackFrame | null] | null => {
    const c =
      chunks.get(chunkStart(t)) ?? (t === duration ? chunks.get(chunkStart(t - 1)) : undefined);
    if (!c || !c.frames.length) return null;
    const fr = c.frames;
    let i = 0;
    while (i + 1 < fr.length && fr[i + 1]!.tick <= t) i++;
    if (fr[i]!.tick > t) return null;
    let b: TrackFrame | null = fr[i + 1] ?? null;
    if (!b && t < duration) {
      const next = chunks.get(chunkStart(t) + SERVER_CHUNK_TICKS);
      b = next?.frames[0] ?? null;
    }
    if (!b && fr[i]!.tick < t && t < c.to) return null;
    return [fr[i]!, b];
  };
  const covered = (t: number) => {
    if (t > duration) return true;
    const c = chunks.get(chunkStart(t));
    if (!c) return false;
    // 조각 끝 틱 넘어는 다음 조각 첫 프레임이 있어야 보간할 수 있다(끝이면 괜찮다)
    return t <= c.to;
  };

  const currentChunk = () => chunks.get(chunkStart(tick));

  const switchToServer = (at: number, why: "build" | "hash") => {
    if (!fetchChunk || playback === "server" || server === "failed") return;
    playback = "server";
    server = chunks.size ? "on" : "loading";
    switchedAt ??= at;
    switchReason ??= why;
    tick = at;
    lastLocalVersion = local.layoutVersion;
    lastOut = local.transforms().slice();
    void fetchAt(chunkStart(at));
    opts.onSwitch?.(at, why);
  };

  const serverLayoutVersion = (): number => {
    const f = framesAt(tick) ?? framesAt(Math.max(0, tick - 1));
    const c = currentChunk();
    if (!f || !c) return lastLocalVersion;
    const v = f[0].layoutVersion;
    if (firstServerVersion === null) {
      firstServerVersion = v;
      // 배치가 같으면 다시 싣지 않는다(장면을 그대로 둔다) · 다르면 새 버전
      const same = JSON.stringify(c.layouts[v]) === JSON.stringify(local.layout);
      layoutBase = same ? lastLocalVersion : lastLocalVersion + 1;
    }
    return layoutBase + (v - firstServerVersion);
  };

  const player: FallbackPlayer = {
    get header() {
      return local.header;
    },
    durationTicks: duration,
    get mode() {
      return local.mode;
    },
    get machines() {
      return local.machines;
    },
    get sections() {
      return local.sections;
    },
    get layout() {
      if (playback !== "server") return local.layout;
      const f = framesAt(tick);
      const c = currentChunk();
      return (f && c?.layouts[f[0].layoutVersion]) || local.layout;
    },
    get layoutVersion() {
      return playback === "server" ? serverLayoutVersion() : local.layoutVersion;
    },
    get tick() {
      return playback === "server" ? tick : local.tick;
    },
    get ended() {
      return playback === "server" ? tick >= duration : local.ended;
    },
    get mismatch() {
      return local.mismatch;
    },
    get playback() {
      return playback;
    },
    get server() {
      return server;
    },
    get buffering() {
      return playback === "server" && !covered(Math.min(duration, tick + 1));
    },
    get switchedAt() {
      return switchedAt;
    },
    get switchReason() {
      return switchReason;
    },
    step() {
      if (playback !== "server") {
        const r = local.step();
        if (local.mismatch !== null && fetchChunk && server !== "failed")
          switchToServer(local.tick, "hash");
        return r;
      }
      if (tick >= duration) return false;
      const c = currentChunk();
      // 절반을 지나면 다음 조각을 미리
      if (c && tick - chunkStart(tick) >= SERVER_CHUNK_TICKS / 2)
        void fetchAt(chunkStart(tick) + SERVER_CHUNK_TICKS);
      if (!covered(tick + 1)) {
        void fetchAt(chunkStart(tick + 1));
        return true; // 기다린다(틱은 그대로)
      }
      tick++;
      return true;
    },
    async seek(t, o) {
      const to = Math.max(0, Math.min(duration, Math.floor(t)));
      if (playback !== "server") return local.seek(to, o);
      tick = to;
      if (covered(to)) return;
      await new Promise<void>((ok) => {
        waiters.push(ok);
        void fetchAt(chunkStart(to));
        o?.signal?.addEventListener("abort", () => ok(), { once: true });
      });
    },
    transforms() {
      if (playback !== "server") return local.transforms();
      const f = framesAt(tick);
      if (!f || !lib) return lastOut ?? local.transforms();
      out = lib.lerpTrack(f[0], f[1], tick, out ?? undefined);
      lastOut = out;
      return out;
    },
    relayTimeline: () => local.relayTimeline(),
    dispose() {
      disposed = true;
      const w = waiters;
      waiters = [];
      for (const f of w) f();
      local.dispose();
    },
  };
  // SR-REC-02 — 기록한 빌드가 지금과 다르면 재생 전에(틱 0 · 입력 재생을 한 틱도 돌리지 않는다)
  if (local.buildDiffers === true) switchToServer(local.tick, "build");
  return player;
}
